import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../../contexts/Web3Context';
import { SmartContract } from '../../types/blockchain';
import blockchainService from '../../services/blockchainService';
import { 
  CurrencyDollarIcon, 
  UsersIcon, 
  ChartBarIcon,
  PlayIcon,
  PauseIcon,
  ArrowUpIcon,
  CogIcon,
  ShieldCheckIcon 
} from '@heroicons/react/24/outline';

const SmartContractManager: React.FC = () => {
  const { wallet } = useWeb3();
  const [contracts, setContracts] = useState<SmartContract[]>([]);
  const [loading, setLoading] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [selectedContract, setSelectedContract] = useState<SmartContract | null>(null);
  const [upgrading, setUpgrading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeVersion, setUpgradeVersion] = useState('');

  useEffect(() => {
    if (wallet?.address) {
      loadData();
    }
  }, [wallet]);

  const loadData = async () => {
    if (!wallet?.address) return;
    
    setLoading(true);
    try {
      // 使用新的统一mock数据
      const { UnifiedMockData } = await import('../../data/mock');
      const mockData = new UnifiedMockData();
      
      const contractsData = await Promise.resolve(mockData.getSmartContracts(wallet.address));
      setContracts(contractsData);
    } catch (error) {
      console.error('Failed to load contract data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeployContract = async () => {
    if (!wallet?.address) return;
    
    setDeploying(true);
    try {
      const participants = [
        { address: wallet.address, name: '主创作者', sharePercentage: 50 },
        { address: '0x456...', name: '制作人', sharePercentage: 30 },
        { address: '0x789...', name: '演唱者', sharePercentage: 20 }
      ];
      
      const contract = await blockchainService.deployRevenueContract('work_new', participants);
      setContracts(prev => [contract, ...prev]);
    } catch (error) {
      console.error('Failed to deploy contract:', error);
    } finally {
      setDeploying(false);
    }
  };


  const handleUpgradeContract = async (contractId: string) => {
    if (!upgradeVersion) {
      alert('请输入升级版本号');
      return;
    }

    setUpgrading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      setContracts(prev => prev.map(contract => 
        contract.id === contractId 
          ? { 
              ...contract, 
              version: upgradeVersion,
              upgradedAt: Date.now()
            }
          : contract
      ));
      
      setShowUpgradeModal(false);
      setUpgradeVersion('');
      alert('合约升级成功！');
    } catch (error) {
      console.error('Failed to upgrade contract:', error);
      alert('合约升级失败，请重试');
    } finally {
      setUpgrading(false);
    }
  };

  const handlePauseContract = async (contractId: string) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setContracts(prev => prev.map(contract => 
        contract.id === contractId 
          ? { ...contract, status: 'paused' as const }
          : contract
      ));
      
      alert('合约已暂停');
    } catch (error) {
      console.error('Failed to pause contract:', error);
      alert('暂停失败，请重试');
    }
  };

  const handleResumeContract = async (contractId: string) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setContracts(prev => prev.map(contract => 
        contract.id === contractId 
          ? { ...contract, status: 'active' as const }
          : contract
      ));
      
      alert('合约已恢复');
    } catch (error) {
      console.error('Failed to resume contract:', error);
      alert('恢复失败，请重试');
    }
  };

  const formatCurrency = (amount: string, currency: string) => {
    return `${parseFloat(amount).toFixed(4)} ${currency}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'paused': return 'text-yellow-600 bg-yellow-100';
      case 'terminated': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (!wallet?.isConnected) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">请先连接钱包以查看智能合约</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">智能合约管理</h1>
        <p className="text-gray-600">部署和管理音乐版权智能合约</p>
        
        {/* 智能合约核心价值展示 */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <h3 className="font-semibold text-indigo-800 mb-2">⚡ 自动化分成执行</h3>
            <p className="text-sm text-indigo-700">智能合约自动执行收益分配，无需人工干预，确保协作者按约定比例实时获得收益。</p>
          </div>
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
            <h3 className="font-semibold text-cyan-800 mb-2">🔄 合约升级管理</h3>
            <p className="text-sm text-cyan-700">支持合约版本升级和功能扩展，保持系统的可维护性和可扩展性。</p>
          </div>
        </div>
      </div>

      {/* 部署新合约 */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">部署新的收益分配合约</h2>
        <div className="flex gap-4">
          <button
            onClick={handleDeployContract}
            disabled={deploying}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <CurrencyDollarIcon className="w-5 h-5" />
            {deploying ? '部署中...' : '部署收益分配合约'}
          </button>
        </div>
      </div>

      {/* 合约列表 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold">我的智能合约</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : contracts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              暂无智能合约
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {contracts.map((contract) => (
                <div 
                  key={contract.id} 
                  className={`p-6 cursor-pointer hover:bg-gray-50 ${
                    selectedContract?.id === contract.id ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => setSelectedContract(contract)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <CurrencyDollarIcon className="w-6 h-6 text-blue-600" />
                      <div>
                        <h3 className="font-medium">收益分配合约</h3>
                        <p className="text-sm text-gray-500 font-mono">
                          {contract.address.substring(0, 20)}...
                        </p>
                        <p className="text-xs text-gray-400">
                          版本: {contract.version || 'v1.0.0'}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(contract.status)}`}>
                      {contract.status === 'active' ? '活跃' : 
                       contract.status === 'paused' ? '暂停' : '终止'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <UsersIcon className="w-4 h-4" />
                      <span>{contract.participants.length} 参与者</span>
                    </div>
                    <div>
                      部署于 {new Date(contract.deployedAt).toLocaleDateString()}
                    </div>
                  </div>
                  
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 合约详情 */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold">合约详情</h2>
          </div>
          
          {selectedContract ? (
            <div className="p-6">
              <div className="mb-6">
                <h3 className="font-medium mb-2">参与者分成比例</h3>
                <div className="space-y-3">
                  {selectedContract.participants.map((participant, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{participant.name}</div>
                        <div className="text-sm text-gray-500 font-mono">
                          {participant.address.substring(0, 20)}...
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">{participant.sharePercentage}%</div>
                        <div className="text-sm text-gray-500">
                          已获得: {formatCurrency(participant.totalEarned, 'SOL')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h3 className="font-medium mb-2">合约操作</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedContract.status === 'active' ? (
                    <button 
                      onClick={() => handlePauseContract(selectedContract.id)}
                      className="flex items-center gap-2 px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-sm hover:bg-yellow-200"
                    >
                      <PauseIcon className="w-4 h-4" />
                      暂停合约
                    </button>
                  ) : selectedContract.status === 'paused' ? (
                    <button 
                      onClick={() => handleResumeContract(selectedContract.id)}
                      className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded text-sm hover:bg-green-200"
                    >
                      <PlayIcon className="w-4 h-4" />
                      恢复合约
                    </button>
                  ) : null}
                  
                  <button 
                    onClick={() => setShowUpgradeModal(true)}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm hover:bg-blue-200"
                  >
                    <ArrowUpIcon className="w-4 h-4" />
                    升级合约
                  </button>
                  
                  <button className="flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-800 rounded text-sm hover:bg-gray-200">
                    <ChartBarIcon className="w-4 h-4" />
                    查看统计
                  </button>
                  
                  <button className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-800 rounded text-sm hover:bg-purple-200">
                    <ShieldCheckIcon className="w-4 h-4" />
                    安全审计
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              选择一个合约查看详情
            </div>
          )}
        </div>
      </div>


      {/* 合约升级模态框 */}
      {showUpgradeModal && selectedContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold mb-4">升级智能合约</h2>
            
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-2">当前合约信息:</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>合约地址:</span>
                  <span className="font-mono">{selectedContract.address.substring(0, 20)}...</span>
                </div>
                <div className="flex justify-between">
                  <span>当前版本:</span>
                  <span className="font-medium">{selectedContract.version || 'v1.0.0'}</span>
                </div>
                <div className="flex justify-between">
                  <span>部署时间:</span>
                  <span>{new Date(selectedContract.deployedAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">新版本号</label>
              <input
                type="text"
                value={upgradeVersion}
                onChange={(e) => setUpgradeVersion(e.target.value)}
                placeholder="例如: v2.0.0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2 text-yellow-800 mb-2">
                <CogIcon className="w-4 h-4" />
                <span className="font-medium">升级说明</span>
              </div>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• 升级将保留所有现有数据和参与者信息</li>
                <li>• 升级过程中合约将暂时暂停</li>
                <li>• 升级完成后需要重新激活合约</li>
                <li>• 建议在升级前进行安全审计</li>
              </ul>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => handleUpgradeContract(selectedContract.id)}
                disabled={upgrading || !upgradeVersion}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {upgrading ? '升级中...' : '确认升级'}
              </button>
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  setUpgradeVersion('');
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartContractManager;
